import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import classnames from "classnames"
import * as brands from "brands"
import * as api from "../api"
import * as routing from "../routing"
import * as v2layouts from "../layouts"
import * as cache from "../cache"
import * as authzc from "authzcached"
import * as icons from "icons"
import * as layouts from "layouts"
import * as inputs from "inputs"
import * as nav from "brandguard/components/navigation"
import Container from "../container"
import { SubNavigation } from "../subnav"
import DocumentUpload from "../components/uploads"
import { UploadSimple } from "brandguard/components"
import { CancellablePromise } from "real-cancellable-promise"

export default function DocumentsContainer(): JSX.Element {
	const cached = cache.useCached()
	const brand = brands.caching.useCached()
	const metaauthz = authzc.useCache((cached) => cached.meta)
	const [uploadFocused, setUploadFocused] = useState(false)

	const UploadDropWell = useMemo(() => {
		return function BrandguideDropWell(props: inputs.uploads.api.Props) {
			return <DocumentUpload background={layouts.theme.colors.blue.blue} {...props} />
		}
	}, [])

	useEffect(() => {
		cached.refreshDocuments()
	}, [])

	return (
		<Container title="Documents" urlgen={routing.documents}>
			<SubNavigation />
			<v2layouts.ContentArea>
				<div style={{ marginBottom: "20px", position: "relative" }}>
					<layouts.buttons.outline
						width="240px"
						height="40px"
						borderRadius="4px"
						onClick={() => setUploadFocused(!uploadFocused)}
						style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
					>
						<icons.FileUpload height="20px" width="20px" />
						<layouts.Span px="10px">Upload Brand Guidelines</layouts.Span>
					</layouts.buttons.outline>
					<div style={{ display: uploadFocused ? "flex" : "none", position: "absolute", top: "100%", paddingTop: "5px", zIndex: 1000 }}>
						<UploadSimple<api.DocumentExtract>
							className={classnames(nav.layouts.slideDownAnimation)}
							title="Upload Brand Guidelines"
							isProcessing={(doc) => doc.parse_status === "processing" || doc.parse_status === "pending"}
							onChange={() => cached.refreshDocuments()}
							resume={() => {
								return CancellablePromise.resolve([])
							}}
							refresh={(doc) => {
								return api.documents.status(brand.id, doc.id, authzc.bearer(metaauthz)).then((result: api.ParseStatusResponse) => {
									if (result.parse_status === "failed" || result.parse_status === "error") {
										return CancellablePromise.reject({ ...doc, parse_status: result.parse_status })
									}
									return CancellablePromise.resolve({ ...doc, parse_status: result.parse_status })
								})
							}}
							upload={(_stats, content) => {
								const formData = new FormData()
								formData.append("file", content)
								formData.append("brand_id", brand.id)
								return api.documents.upload(brand.id, formData, authzc.bearer(metaauthz)).then((response) => response.document!)
							}}
							DropWell={UploadDropWell}
							onClose={() => setUploadFocused(false)}
						/>
					</div>
				</div>

				{cached.documents.length === 0 ? (
					<v2layouts.EmptyState>
						<v2layouts.EmptyStateIcon>
							<icons.FileUpload />
						</v2layouts.EmptyStateIcon>
						<v2layouts.EmptyStateTitle>No documents uploaded yet</v2layouts.EmptyStateTitle>
						<v2layouts.EmptyStateText>
							Upload your brand guidelines to automatically extract logos, colors, fonts, and imagery
						</v2layouts.EmptyStateText>
					</v2layouts.EmptyState>
				) : (
					<v2layouts.GridContainer>
						{cached.documents.map((doc) => (
							<DocumentCard key={doc.id} document={doc} onRefresh={cached.refreshDocuments} />
						))}
					</v2layouts.GridContainer>
				)}
			</v2layouts.ContentArea>
		</Container>
	)
}

function DocumentCard({
	document,
	onRefresh,
}: {
	document: api.DocumentExtract
	onRefresh: () => Promise<void>
}): JSX.Element {
	const navigate = useNavigate()
	const brand = brands.caching.useCached()
	const metaauthz = authzc.useCache((cached) => cached.meta)
	const [deleting, setDeleting] = useState(false)

	const handleDelete = async (e: React.MouseEvent): Promise<void> => {
		e.stopPropagation()

		setDeleting(true)
		try {
			await api.documents.destroy(brand.id, document.id, authzc.bearer(metaauthz))
			await onRefresh()
		} catch (err) {
			console.error("failed to delete document", err)
		} finally {
			setDeleting(false)
		}
	}

	return (
		<v2layouts.Card onClick={() => navigate(routing.review(brand.id, document.id))}>
			<v2layouts.CardContent>
				<>
					<v2layouts.CardTitle title={document.original_filename}>{document.original_filename}</v2layouts.CardTitle>
					{document.created_at && (
						<v2layouts.CardMeta style={{ fontSize: "11px", marginTop: "4px" }}>
							Uploaded: {new Date(document.created_at).toLocaleDateString()}
						</v2layouts.CardMeta>
					)}
					{document.file_size && (
						<v2layouts.CardMeta style={{ fontSize: "11px", marginTop: "4px" }}>
							Size: {(Number(document.file_size) / 1024 / 1024).toFixed(2)}MB
						</v2layouts.CardMeta>
					)}

					<div
						style={{
							marginTop: "15px",
							display: "flex",
							alignItems: "center",
							cursor: deleting ? "not-allowed" : "pointer",
							opacity: deleting ? 0.5 : 1,
						}}
						onClick={deleting ? undefined : handleDelete}
						title={deleting ? "Deleting..." : "Delete document"}
					>
						<icons.Trash width="18px" height="18px" />
					</div>

				</>
			</v2layouts.CardContent>
		</v2layouts.Card>
	)
}
